import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const phases = [
  {
    phase: '课前准备',
    icon: 'ri-calendar-check-line',
    color: 'primary' as const,
    items: ['教学目标与大纲检查', 'PPT与问题链建议', '案例和权威资料推荐'],
  },
  {
    phase: '课堂实施',
    icon: 'ri-presentation-line',
    color: 'accent' as const,
    items: ['课堂证据卡', '可打磨片段', '实时教学提示'],
  },
  {
    phase: '课后复盘',
    icon: 'ri-file-list-3-line',
    color: 'primary' as const,
    items: ['导入有效性分析', '提问回应诊断', '案例理论转化评估', '时间投入产出比'],
  },
  {
    phase: '长期成长',
    icon: 'ri-line-chart-line',
    color: 'accent' as const,
    items: ['成长曲线', '维度趋势', '稳定能力与反复问题', '改进前后对比'],
  },
];

export default function P62ServiceStudent({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />服务教师
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            备课、授课、复盘和成长<span className="text-primary-600">一体化</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            教师端的核心不是多一份报告，而是让每一次评价都能推动下一次教学
          </p>
        </div>

        {/* Teaching Cycle */}
        <div className={`max-w-5xl mx-auto mb-4 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {phases.map((p, i) => (
              <div
                key={p.phase}
                className="p-4 md:p-5 glass-liquid rounded-xl"
                style={{ transitionDelay: `${300 + i * 120}ms` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl ${p.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${p.icon} text-lg ${p.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                  </div>
                  <div>
                    <span className="text-sm font-body font-bold text-foreground-800">{p.phase}</span>
                    {i < phases.length - 1 && (
                      <div className="hidden lg:block">
                        <i className="ri-arrow-right-line text-xs text-foreground-300 ml-1" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {p.items.map((item) => (
                    <div key={item} className="flex items-start gap-1.5 text-xs text-foreground-600 font-body">
                      <i className="ri-check-line text-xs text-gold-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cycle flow indicator */}
        <div className={`max-w-md mx-auto mb-4 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center justify-center gap-2 text-xs font-body text-foreground-400">
            <i className="ri-loop-left-line text-gold-500" />
            <span>教学周期闭环：课前 → 课中 → 课后 → 长期成长</span>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              当教师有多节课程进入系统以后，单课结果会形成<strong className="text-gold-700">连续成长轨迹</strong>。教师看到的不只是"这一次讲得怎么样"，而是哪些能力长期稳定、哪些问题反复出现、哪些维度经过改进后已经发生变化。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}